import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Header } from "@/components/pepmuse/Header";
import { Footer } from "@/components/pepmuse/Footer";
import { MobileCTA } from "@/components/pepmuse/MobileCTA";
import { InquiryDialog } from "@/components/pepmuse/InquiryDialog";
import { initSmoothScroll, scrollToTop } from "@/components/pepmuse/scroll";
import Home from "@/pages/Home";
import BusinessInABox from "@/pages/BusinessInABox";
import WebsiteCreation from "@/pages/WebsiteCreation";
import LaunchChecklist from "@/pages/LaunchChecklist";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import ResetPassword from "@/pages/ResetPassword";
import ComplianceChecker from "@/pages/ComplianceChecker";
import Resources from "@/pages/Resources";
import ResourceArticle from "@/pages/ResourceArticle";
import AuthCallback from "@/pages/AuthCallback";
import { captureAttribution, trackPageview, trackGaPageview } from "@/components/pepmuse/analytics";
import { AuthProvider } from "@/components/pepmuse/AuthContext";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    scrollToTop();
  }, [pathname]);
  return null;
};

const Shell = () => {
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const openerRef = useRef(null);
  const location = useLocation();
  const { pathname } = location;

  const openInquiry = useCallback((nextPrefill) => {
    openerRef.current = document.activeElement;
    const valid =
      nextPrefill &&
      typeof nextPrefill === "object" &&
      ("need" in nextPrefill || "idea" in nextPrefill)
        ? nextPrefill
        : null;
    setPrefill(valid);
    setInquiryOpen(true);
  }, []);

  const handleInquiryChange = useCallback((open) => {
    setInquiryOpen(open);
    if (!open && openerRef.current instanceof HTMLElement) {
      setTimeout(() => openerRef.current?.focus(), 0);
    }
  }, []);

  useEffect(() => initSmoothScroll(), []);

  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    trackPageview(pathname);
    trackGaPageview(pathname);
  }, [pathname]);

  const isHome = pathname === "/";

  // OAuth return: process session_id FIRST, synchronously during render,
  // before any route or auth check can run (prevents race conditions).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <div className="min-h-screen bg-ink font-sans text-paper antialiased">
      <a
        href="#main"
        data-testid="skip-link"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-lilac focus:px-5 focus:py-2.5 focus:text-sm focus:font-bold focus:text-ink"
      >
        Skip to content
      </a>

      <ScrollManager />
      <Header onBuild={() => openInquiry()} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <main id="main" className={isHome ? "pb-24 md:pb-0" : ""}>
        <Routes>
          <Route path="/" element={<Home onBuild={() => openInquiry()} />} />
          <Route path="/business-in-a-box" element={<BusinessInABox onBuild={openInquiry} />} />
          <Route path="/website-creation" element={<WebsiteCreation onBuild={openInquiry} />} />
          <Route path="/checklist" element={<LaunchChecklist onBuild={openInquiry} />} />
          <Route path="/account" element={<Account />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/compliance-checker" element={<ComplianceChecker onBuild={openInquiry} />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:slug" element={<ResourceArticle onBuild={openInquiry} />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />

      {isHome && <MobileCTA onBuild={() => openInquiry()} suppressed={inquiryOpen || menuOpen} />}
      <InquiryDialog open={inquiryOpen} onOpenChange={handleInquiryChange} prefill={prefill} />
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}
